/**
 * Input Component
 *
 * Glass-inset styled text input with error states.
 * Uses CSS variables for theme adaptation.
 */

import React from 'react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  hint?: string;
}

export const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ label, error, hint, className = '', id, ...props }, ref) => {
    const inputId = id || `input-${Math.random().toString(36).slice(2, 11)}`;

    const baseClasses = `
      w-full px-4 py-3
      border rounded-xl
      bg-[rgba(0,0,0,0.03)]
      text-[var(--text-primary)] placeholder-[var(--text-tertiary)]
      transition-all duration-150
      focus:ring-2 focus:border-transparent focus:outline-none
      disabled:opacity-50 disabled:cursor-not-allowed
    `;

    const stateClasses = error
      ? 'border-[#FF3B30]/50 focus:ring-[#FF3B30]/20'
      : 'border-[var(--border-default)] focus:ring-[#1A1A1A]/20 focus:border-[#1A1A1A]/40';

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-[var(--text-secondary)] mb-2"
          >
            {label}
          </label>
        )}
        <input
          ref={ref}
          id={inputId}
          className={`${baseClasses} ${stateClasses} ${className}`.trim().replace(/\s+/g, ' ')}
          {...props}
        />
        {error && (
          <p className="mt-1.5 text-sm text-[#FF3B30]">{error}</p>
        )}
        {hint && !error && (
          <p className="mt-1.5 text-sm text-[var(--text-tertiary)]">{hint}</p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
